import axios, { AxiosError } from 'axios';
import { authService } from './authService';
import { apiHealthService } from './apiHealthService';

interface UserProfile {
    firstName: string;
    lastName: string;
    email: string;
}

interface PasswordChange {
    currentPassword: string;
    newPassword: string;
}

const API_URL = process.env.REACT_APP_API_URL || `${apiHealthService.getApiUrl()}/api`;

// Function to check if the server is available
const checkServerAvailability = async (): Promise<boolean> => {
    return apiHealthService.isAvailable(true); // Force a fresh check
};

// Create axios instance with custom config
const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 15000, // 15 seconds
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    validateStatus: function (status) {
        return status >= 200 && status < 500;
    }
});

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = authService.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('Making request with config:', {
            url: config.url,
            method: config.method,
            headers: config.headers
        });
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for debugging
axiosInstance.interceptors.response.use(
    (response) => {
        console.log('Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
        return response;
    },
    (error) => {
        console.error('Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            headers: error.response?.headers
        });

        if (error.response?.status === 401 || error.response?.status === 302) {
            console.log('Authentication required, redirecting to login...');
            authService.logout();
            window.location.href = '/login';
            return Promise.reject(new Error('Please log in to access this resource'));
        }
        return Promise.reject(error);
    }
);

class UserService {
    async getUserProfile(): Promise<UserProfile> {
        try {
            // Check if we have a token first
            const token = authService.getToken();
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            console.log('Fetching user profile with token:', token.substring(0, 10) + '...');
            const response = await axiosInstance.get<UserProfile>('/users/profile');
            
            if (response.status === 404) {
                throw new Error('Profile endpoint not found');
            }
            
            if (!response.data) {
                throw new Error('No profile data received');
            }

            console.log('Profile response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            const axiosError = error as AxiosError;
            if (axiosError.response?.status === 401 || axiosError.response?.status === 302) {
                throw new Error('Please log in to view your profile');
            }
            throw new Error('Error loading profile: ' + (axiosError.response?.data || axiosError.message));
        }
    }

    async updateProfile(profile: UserProfile): Promise<void> {
        try {
            const token = authService.getToken();
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            await axiosInstance.put('/users/profile', profile);
        } catch (error) {
            console.error('Error updating profile:', error);
            const axiosError = error as AxiosError;
            throw new Error('Error updating profile: ' + (axiosError.response?.data || axiosError.message));
        }
    }

    async changePassword(passwordData: PasswordChange): Promise<void> {
        try {
            const token = authService.getToken();
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            await axiosInstance.post('/users/change-password', passwordData);
        } catch (error) {
            console.error('Error changing password:', error);
            const axiosError = error as AxiosError;
            throw new Error('Error changing password: ' + (axiosError.response?.data || axiosError.message));
        }
    }
}

export const userService = new UserService();
export default userService; 